import React, { useEffect, useState } from "react";

import { pdf } from "@react-pdf/renderer";
import { Button } from "antd";
import Modal from "antd/es/modal/Modal";
import printJS from "print-js";
import Scrollbar from "react-scrollbars-custom";
import { toast } from "react-toastify";

import ReceiptModalActionsBar from "../../SampleSubmission/SampleSubmissionModal/SubmissionReceiptModal/ReceiptModalActionsBar";
import ReceiptModalHeader from "../../SampleSubmission/SampleSubmissionModal/SubmissionReceiptModal/ReceiptModalHeader";
import ReceiptPDF from "../../SampleSubmission/SampleSubmissionModal/SubmissionReceiptModal/ReceiptPDF/ReceiptPDF";
import SamplePageHeader from "../../SampleSubmission/SampleSubmissionModal/SubmissionReceiptModal/SamplePageHeader";
import SamplesInfo from "../../SampleSubmission/SampleSubmissionModal/SubmissionReceiptModal/SamplesInfo";
import SamplesList from "../../SampleSubmission/SampleSubmissionModal/SubmissionReceiptModal/SamplesList";
import SenderInfo from "../../SampleSubmission/SampleSubmissionModal/SubmissionReceiptModal/SenderInfo";

import { getCustomerAddress } from "../../../actions/sampleSubmission";

import { ReactComponent as CloseIcon } from "../../../assets/images/sample-submission/close.svg";
// import PdfPreview from "../../../Common/PdfPreview";

import "../../SampleSubmission/SampleSubmissionModal/SubmissionReceiptModal/SubmissionReceiptModal.css";

const FOOTER_TEXT = [
  "© 2023 ESV, All rights reserved",
  "Genista Biosciences, Inc. 5500 Hellyer Avenue, San Jose, CA. 95138 P. 1.408.934.6575",
];

const CUSTOMER_INFO_FIELDS = [
  { label: "Attn", key: "user_name" },
  { label: "Company", key: "company" },
  { label: "Phone", key: "telephone" },
  { label: "Fax", key: "fax" },
  { label: "Email", key: "user_email" },
  { label: "Address", key: "address_concat" },
];

const CUSTOMER_INFO_FIELDS_BY_ROW = [
  [
    { label: "Attn", key: "user_name" },
    { label: "Company", key: "company" },
  ],
  [
    { label: "Phone", key: "telephone" },
    { label: "Fax", key: "fax" },
  ],
  [
    { label: "Email", key: "user_email" },
    { label: "Address", key: "address_concat" },
  ],
];

export default function CalendarReceiptModal({
  showReceipt,
  handleReceipt,
  submissionFromParent = null,
  // submissionId,
  customerId,
  closable = true,
  maskClosable = true,
  headerTitleText,
  // po, // needed because po is not included in getSubmission api response
}) {
  const sampleCategory = "environment";
  const [loadingCustomerInfo, setLoadingCustomerInfo] = useState(true);
  const [customerInfo, setCustomerInfo] = useState({});
  const [submission] = useState(submissionFromParent);
  const [loadingSubmission] = useState(!submissionFromParent);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  // const [blobUrl, setBlobUrl] = useState("");

  const getCustomerInfo = async () => {
    const result = await getCustomerAddress(customerId, sampleCategory);
    if (result.success) {
      const addressArr = [];
      if (result.address.address_1) {
        addressArr.push(result.address.address_1);
      }
      if (result.address.address_2) {
        addressArr.push(result.address.address_2);
      }
      result.address.address_concat = addressArr.join(", ");
      setCustomerInfo(result.address);
    } else {
      toast.error(result.message);
    }
    setLoadingCustomerInfo(false);
  };

  useEffect(() => {
    getCustomerInfo();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getFileName = () => {
    const companyName = customerInfo.company;
    const submissionName = submission.submit_name;
    const timestamp = Date.now();
    const fileName = `${companyName}_${submissionName}_${timestamp}.pdf`;
    const sanitizedFileName = fileName.replace(/\s|\//g, "_");
    return sanitizedFileName;
  };

  const generatePDFBlob = async () => {
    try {
      setGeneratingPDF(true);
      const pdfBlob = await pdf(ReceiptPDF({
        submission, customerInfo, CUSTOMER_INFO_FIELDS_BY_ROW, FOOTER_TEXT, getFileName,
      })).toBlob();
      setGeneratingPDF(false);
      return pdfBlob;
    } catch (e) {
      setGeneratingPDF(false);
      toast.error("Failed to generate pdf");
      return null;
    }
  };

  const handlePrint = async () => {
    const pdfBlob = await generatePDFBlob();
    if (pdfBlob) {
      const url = URL.createObjectURL(pdfBlob);
      printJS(url);
      // setBlobUrl(url);
    }
  };

  const handleDownload = async () => {
    const pdfBlob = await generatePDFBlob();
    if (pdfBlob) {
      const url = window.URL.createObjectURL(pdfBlob);
      if (url) {
        const a = document.createElement("a");
        a.href = url;
        a.download = getFileName();
        a.click();
      }
    }
  };

  const handleOnCancel = () => {
    handleReceipt();
  };
  return (
    <Modal
      title={(
        <ReceiptModalActionsBar
          loading={loadingCustomerInfo || loadingSubmission || generatingPDF}
          headerTitleText={headerTitleText}
          handlePrint={handlePrint}
          handleDownload={handleDownload}
          closable={closable}
        />
      )}
      maskClosable={maskClosable}
      closeIcon={<CloseIcon />}
      open={showReceipt}
      onCancel={handleOnCancel}
      centered
      destroyOnClose
      footer={closable ? null : <Button type="primary" onClick={handleOnCancel}>Done</Button>}
      wrapClassName="SubmissionReceiptModal"
    >
      <Scrollbar className="SubmissionReceiptModal__Scrollbar">
        <div className="SubmissionReceiptModal__Content">
          <div className="SubmissionReceiptModal__Container">
            <ReceiptModalHeader submission={submission} />
            <SamplesInfo submission={submission} customerInfo={customerInfo} />
            <SenderInfo CUSTOMER_INFO_FIELDS={CUSTOMER_INFO_FIELDS} customerInfo={customerInfo} />
          </div>
          <div className="SubmissionReceiptModal__Footer">
            {FOOTER_TEXT.map((text) => (
              <span key={text}>{text}</span>
            ))}
          </div>
        </div>
        <div className="SubmissionReceiptModal__Content">
          <div className="SubmissionReceiptModal__Container">
            <SamplePageHeader />
            <SamplesList submission={submission} />
          </div>
        </div>
      </Scrollbar>
      {/* {blobUrl && ( // for debugging pdf
        <Modal
          open
          width="800px"
          onCancel={() => setBlobUrl("")}
          onOk={() => setBlobUrl("")}
        >
          <PdfPreview
            blob={blobUrl}
            height="70vh"
            scaleNumber={1}
          />
        </Modal>
      )} */}
    </Modal>
  );
}
